import { useCallback, useEffect, useState } from "react";

import { useLatest } from "./use-latest";

export interface DefaultOptions {
  // Set this to all if other params should be removed on change to a string array if you want a specific one to be removed
  clearOthers?: "all" | string[];
  // Set this to true if you don't ever want the history of the browser to be updated
  dontEverSaveInHistory?: boolean;
}

interface UsingSearchParamsOptions extends DefaultOptions {
  // Pass searchParams from a router hook (e.g. react-router's useSearchParams) so
  // that the component re-renders whenever the URL changes — this hook reads the
  // actual values from window.location.search directly, but needs a reactive
  // dependency to know when to re-evaluate. When this is provided, the URL is the
  // source of truth: if the key is already present in the URL it overrides initialValue.
  searchParams: URLSearchParams;
  setSearchParams: (params: URLSearchParams) => void;
}

export type UseSearchStateOptions = DefaultOptions | UsingSearchParamsOptions;

export type SetStateOptions = {
  // Set this to all if other params should be removed on change to a string array if you want a specific one to be removed
  clearOthers?: "all" | string[];
  // Set this to true if you want the update you are doing to not be saved in the history of the browser
  dontSaveInHistory?: boolean;
};

export type SearchValue = null | undefined | string | number | boolean;

// Maps the initialValue type to its primitive base type so the hook's return
// type is practical to use: e.g. literal `0` becomes `number`, `"tab"` becomes
// `string`, `true` becomes `boolean`. Arrays are left as-is.
type PrimitiveSearchValue<T extends SearchValue | SearchValue[]> = T extends boolean
  ? boolean
  : T extends string
  ? string
  : T extends number
  ? number
  : T;

export type SearchStateReturn<T extends SearchValue | SearchValue[]> = [
  PrimitiveSearchValue<T>,
  (
    state:
      | PrimitiveSearchValue<T>
      | ((prevState: PrimitiveSearchValue<T>) => PrimitiveSearchValue<T>),
    setStateOptions?: SetStateOptions
  ) => void
];

const EMPTY_OPTIONS: UseSearchStateOptions = {};

/** Serialises `newState` into a new URLSearchParams, applying clearOthers rules.
 * Returns `null` if the resulting params would be identical to the current URL. */
function buildSearchParams(
  key: string,
  newState: SearchValue | SearchValue[],
  initialValue: SearchValue | SearchValue[],
  clearOthers: "all" | string[] | undefined
): URLSearchParams {
  const params =
    clearOthers === "all" ? new URLSearchParams() : new URLSearchParams(window.location.search);

  if (Array.isArray(clearOthers)) clearOthers.forEach((k) => params.delete(k));

  // Remove the key when the value is back to its default — keeps the URL clean.
  if (newState === initialValue || JSON.stringify(newState) === JSON.stringify(initialValue)) {
    params.delete(key);
  } else if (Array.isArray(newState)) {
    // Arrays are serialised as a comma-separated string, e.g. "a,b,c".
    params.set(key, (newState as SearchValue[]).join(","));
  } else {
    params.set(key, String(newState));
  }

  return params;
}

export type SearchStateAccept<T extends SearchValue> = T extends boolean
  ? "boolean"
  : T extends number
  ? "number"
  : T extends string
  ? "string"
  : T extends null
  ? "null"
  : T extends undefined
  ? "undefined"
  : never;

function parseParam(param: string, accepts: SearchStateAccept<SearchValue>[] = []): SearchValue {
  if (param === "true" && accepts.includes("boolean")) return true;
  if (param === "false" && accepts.includes("boolean")) return false;
  if (param === "null" && accepts.includes("null")) return null;
  if (param === "undefined" && accepts.includes("undefined")) return undefined;
  if (!isNaN(Number(param)) && accepts.includes("number")) return Number(param);
  if (accepts.includes("string")) return param; // Keep other strings as is

  console.warn(
    `Search state value "${param}" does not match any of the accepted types: ${accepts.join(
      ", "
    )}. Returning the raw string.`
  );
  return param; // Fallback: return the raw string if no accepted type matches
}

export function inferAcceptsFromInitialValue(
  initialValue: SearchValue | SearchValue[]
): SearchStateAccept<SearchValue>[] {
  // Defensive fallback: arrays should use the explicit-accepts overload.
  if (Array.isArray(initialValue)) return ["string"];
  if (initialValue === null) return ["null"];
  if (initialValue === undefined) return ["undefined"];
  if (typeof initialValue === "boolean") return ["boolean"];
  if (typeof initialValue === "number") return ["number"];
  return ["string"];
}

// You are either allowed to define a value or an array of values. Otherwise we can't make the array look nice in the url.

export function useSearchState(
  key: string,
  initialValue: boolean,
  options: UseSearchStateOptions
): SearchStateReturn<boolean>;

export function useSearchState(
  key: string,
  initialValue: string,
  options: UseSearchStateOptions
): SearchStateReturn<string>;

export function useSearchState(
  key: string,
  initialValue: number,
  options: UseSearchStateOptions
): SearchStateReturn<number>;

export function useSearchState(
  key: string,
  initialValue: null,
  options: UseSearchStateOptions
): SearchStateReturn<null>;

export function useSearchState(
  key: string,
  initialValue: undefined,
  options: UseSearchStateOptions
): SearchStateReturn<undefined>;

export function useSearchState<T extends SearchValue>(
  key: string,
  initialValue: T,
  options: UseSearchStateOptions
): SearchStateReturn<T>;

export function useSearchState<T extends SearchValue>(
  key: string,
  initialValue: T,
  accepts: SearchStateAccept<T>[],
  options: UseSearchStateOptions
): SearchStateReturn<T>;

export function useSearchState<T extends SearchValue[]>(
  key: string,
  initialValue: T,
  accepts: SearchStateAccept<T[number]>[],
  options: UseSearchStateOptions
): SearchStateReturn<T>;

export function useSearchState<T extends SearchValue | SearchValue[]>(
  key: string,
  initialValue: T,
  acceptsOrOptions: SearchStateAccept<SearchValue>[] | UseSearchStateOptions,
  maybeOptions?: UseSearchStateOptions
): SearchStateReturn<T> {
  const accepts = Array.isArray(acceptsOrOptions)
    ? acceptsOrOptions
    : inferAcceptsFromInitialValue(initialValue);
  const options = Array.isArray(acceptsOrOptions)
    ? maybeOptions ?? EMPTY_OPTIONS
    : acceptsOrOptions;

  const warnOnInvalidValueRef = useLatest((value: SearchValue | SearchValue[]) => {
    if (Array.isArray(initialValue)) {
      (value as SearchValue[]).forEach((stateItem) => {
        if (typeof stateItem === "string" && stateItem.includes(",")) {
          console.warn(
            `Search state string array items are not allowed to include a ",". The state with key "${key}" is now broken.`
          );
        }
      });
    }
  });

  useEffect(() => {
    warnOnInvalidValueRef.current(initialValue);
  }, [warnOnInvalidValueRef, initialValue]);

  const isUsingSearchParams = "searchParams" in options;

  // The value that the URL currently encodes for this key, or initialValue if the
  // key is absent or URL sync is disabled. Re-computed every render so the
  // source-of-truth sync check below always has an up-to-date URL snapshot.
  const urlStateValue = (() => {
    const locationSearch = new URLSearchParams(window.location.search);
    if (!isUsingSearchParams || !locationSearch.has(key)) return initialValue;

    // Parse the raw string from the URL back to the correct primitive type.
    const param = locationSearch.get(key);
    if (param === undefined || param === null) return param;

    // Arrays are serialised as a comma-separated string, e.g. "a,b,c".
    if (Array.isArray(initialValue)) {
      const paramArray = param.split(",");
      return paramArray.map((item) => parseParam(item, accepts));
    }

    // Single value: parse the raw string back to its original primitive type
    // (boolean, number, null, undefined, or string).
    return parseParam(param, accepts);
  })() as PrimitiveSearchValue<T>;

  const [state, actualSetState] = useState<PrimitiveSearchValue<T>>(urlStateValue);

  // Keep local state in sync with the URL. If something outside this hook changes
  // the URL (e.g. another hook calling clearOthers: "all", or a back-navigation),
  // urlStateValue will diverge from state. Calling setState here during render is
  // the standard React pattern for derived-state sync: React discards this render
  // and immediately re-renders with the corrected value.
  if (
    isUsingSearchParams &&
    state !== urlStateValue &&
    JSON.stringify(state) !== JSON.stringify(urlStateValue)
  ) {
    actualSetState(urlStateValue);
  }

  const searchParamOptions = isUsingSearchParams ? (options as UsingSearchParamsOptions) : null;

  const setState = useCallback(
    (
      _state:
        | PrimitiveSearchValue<T>
        | ((prevState: PrimitiveSearchValue<T>) => PrimitiveSearchValue<T>),
      setStateOptions?: SetStateOptions
    ) => {
      // Resolve new state using the current state so we avoid a functional updater.
      // Functional updaters run during React's render phase; calling setSearchParams
      // inside one triggers "Cannot update RouterProvider while rendering" warnings.
      const newState = typeof _state === "function" ? _state(state) : _state;

      if (newState === state) return; // No change, no need to update

      warnOnInvalidValueRef.current(newState);

      // In local-only mode, just update React state and return. We intentionally
      // do not touch the URL here so that react-router's useBlocker can intercept
      // navigation (useBlocker only fires on URL changes, not on setState calls).
      if (!searchParamOptions) {
        actualSetState(newState);
        return;
      }

      const clearOthers = setStateOptions?.clearOthers ?? options.clearOthers;
      const newSearchParams = buildSearchParams(key, newState, initialValue, clearOthers);

      // If the serialised URL would be identical, skip the router update entirely.
      // We still call actualSetState so the component reflects the new value immediately
      // rather than waiting for a URL-driven re-render.
      const searchParamsHaveChanged =
        newSearchParams.toString() !== new URLSearchParams(window.location.search).toString();

      if (!searchParamsHaveChanged) {
        actualSetState(newState);
        return;
      }

      const saveInHistory = !options.dontEverSaveInHistory && !setStateOptions?.dontSaveInHistory;

      if (saveInHistory) {
        // Call setSearchParams directly — NOT inside a state updater — so React
        // does not see a RouterProvider update during the current component's render.
        // Local state will sync on the next render via the URL source-of-truth check above.
        searchParamOptions.setSearchParams(newSearchParams);
      } else {
        window.history.replaceState(
          null,
          "",
          window.location.origin + window.location.pathname + "?" + newSearchParams
        );
        actualSetState(newState);
      }
    },
    [warnOnInvalidValueRef, searchParamOptions, initialValue, key, options, state]
  );

  return [state, setState];
}
